import Tabs from '@cas/components/tabs/tabs/Tabs';
import useTabs, { TabConfig } from '@cas/components/tabs/tabs/useTabs';
import { useMemo } from 'react';
import { BarChartByProps, ScenarioConfig } from '../config';
import { useTranslation } from 'react-i18next';
import { ChartCard } from '../chart-card/ChartCard';

interface ChartScenariosProps extends Omit<BarChartByProps, 'scenario'> {
  title: string;
  scenarioConfigs: ScenarioConfig[];
  getDataSetLabel: (label: string) => string;
}

export function ChartScenarios({
  title: cardTitle,
  scenarioConfigs,
  colorScheme,
  customColorSchemeFunc,
  getDataSetLabel,
  includeTimeRange,
  timeRange,
  tooltipTransKey,
  includeAssetId,
  assetId,
  filterGroup,
}: ChartScenariosProps) {
  const { t } = useTranslation();

  // Generate tabConfigs from scenarioConfigs
  const tabConfigs = useMemo<TabConfig[]>(() => {
    const commonProps = {
      colorScheme,
      customColorSchemeFunc,
      getDataSetLabel,
      includeTimeRange,
      timeRange,
      tooltipTransKey,
      includeAssetId,
      assetId,
      filterGroup,
    };

    return scenarioConfigs.map(
      ({ title, ChartComponent, scenario, enableCustomTimeRange }: ScenarioConfig) => ({
        title,
        view: (
          <ChartComponent
            {...commonProps}
            scenario={scenario}
            enableCustomTimeRange={enableCustomTimeRange}
          />
        ),
      }),
    );
  }, [
    colorScheme,
    customColorSchemeFunc,
    scenarioConfigs,
    getDataSetLabel,
    includeTimeRange,
    timeRange,
    tooltipTransKey,
    includeAssetId,
    assetId,
    filterGroup,
  ]);

  const { tabs, activeTab, activeView, onSelectTab } = useTabs({ tabConfigs });

  const showTooltip = activeTab !== undefined && !scenarioConfigs[activeTab].hideResultsLimited;
  const tooltip = showTooltip ? t('ShowingLimitedResults', { limit: 10 }) : undefined;

  return (
    <ChartCard
      title={cardTitle}
      tooltip={tooltip}
      controls={<Tabs tabs={tabs} activeTab={activeTab} onSelectTab={onSelectTab} size="small" />}
    >
      {activeView}
    </ChartCard>
  );
}

export default ChartScenarios;
